import _ from "lodash";
import { removeEmptyEntries } from "@lib-shared/lib";
import Messages from "core/constants/messages";
import dialogPolyfill from "dialog-polyfill";

const getButtonCssClassName = (color = "black") => {
    const cssClassNames = ["btn"];
    cssClassNames.push(`btn-${color}`);

    return cssClassNames.join(" ");
}

export const ModalIcon = {
    error: "failure", //"warning",
    info: "info", //fix-errors",
    tick: "success",//"wk-dstv-tick-in-circle"
    warning: "warning"//exclamation mark
}

const getBodyHtml = (body: string | Array<string> | null | undefined, useDefaultContainerInBodyArray: boolean) => {
    const isBodyAnArray = _.isArray(body);

    // Need to revisit - doesn't look right with centred text
    // if (!isBodyAnArray && _.endsWith(body, "...")) {
    //     body = body.substring(0, body.length - 3);
    //     bodyHtml = `<p class="loading-ellipsis">${body}</p>`;
    // } else 

    if (_.isNil(body) || body === "") {
        return "";
    }

    if (!isBodyAnArray && _.isString(body) && body.trim().startsWith("<p>")) {
        return body;
    }

    const lines = isBodyAnArray ? body : [body];
    return useDefaultContainerInBodyArray
        ? lines.map(x => `<p>${x}</p>`).join("")
        : lines.join("");
}

export type ShowModalProps = {
    title?: string,
    body?: string | string[] | null | undefined,
    buttonText?: string,
    open?: boolean,
    icon?: string,
    showButton?: boolean,
    showClose?: boolean,
    showCancel?: boolean,
    onClose?: any | null,
    onCancel?: any | null,
    useDefaultContainerInBodyArray?: boolean,
    appendSpinner?: boolean,
    bodyTitle?: string,
    id?: string
}

export const ShowModal = (props: ShowModalProps) => {
    const {
        title = "",
        body = "",
        buttonText = "Done",
        open = true,
        icon = ModalIcon.tick,
        showButton = true,
        showClose = false,
        showCancel = false,
        onClose = null,
        onCancel = null,
        useDefaultContainerInBodyArray = true,
        appendSpinner = false,
        id
    } = props;

    const buttonCssClassName = getButtonCssClassName();

    let bodyHtml = getBodyHtml(body, useDefaultContainerInBodyArray);

    if (appendSpinner) {
        //bodyHtml = `${bodyHtml}<br/><div role="status" class="default-spinner spinner-border text-light centered-spinner"><span class="visually-hidden">Loading...</span></div>`;
        bodyHtml = `<ul class="square-loader">
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
	<li></li>
</ul>

<h4>Please wait</h4>

${bodyHtml}
`;
    } else {
        const lines = [
            `<i class="icon-${icon}"></i>`,
            title ? `<h4>${title}</h4>` : null,
            bodyHtml
        ];

        bodyHtml = removeEmptyEntries(lines).join("\n");
    }

    //     const htmlHeader = `<section class="dialog-header">
    //     <button class="close">
    //     <i class="icon-close close"></i>
    // </button>
    // </section>`;

    if (showClose) {
        bodyHtml = `<button class="close">
     <i class="icon-close close"></i>
     </button>` + bodyHtml;
    }

    const htmlBody = `<section class="dialog-body">
${bodyHtml}
<section class="dialog-footer">
    <button class="${buttonCssClassName}">${buttonText}</button>
</section>
</section>`;

    const html = [];

    if (showClose) {
        //html.push(htmlHeader);
    }

    html.push(htmlBody);

    const dialog = document.createElement("dialog");

    //Note: It's possible to add the same dialog twice due to the way React renders if not called by an event
    //such as a click event
    if (id) {
        dialog.id = id;

        const existingDialog = document.getElementById(id);

        if (existingDialog) {
            // console.error(`A dialog already exists with id ${id}`);
            return existingDialog as HTMLDialogElement;
        }
    }

    dialog.className = "ui-modal-dialog";
    dialog.innerHTML = html.join("");

    const eventHandler = (eventToHandle: any, name: string) => {
        if (eventToHandle != null) {
            try {
                eventToHandle();
            } catch (error) {
                console.error("Error calling " + name, error);
            }
        }

        ModalAlert.close(dialog);
    }

    const closeHandler = () => eventHandler(onClose, "onClose");

    dialog.querySelectorAll("button").forEach(button => {
        button.addEventListener("click", closeHandler);
    });

    //These elements should always be present. The ?? stop the possible null errors.
    const footer = dialog.querySelector(".dialog-footer") ?? new HTMLElement();
    const doneButton = footer.querySelector("button") ?? new HTMLButtonElement();

    doneButton.addEventListener("click", closeHandler);

    if (!showButton) {
        footer.removeChild(doneButton);
    }

    if (showCancel) {
        const cancelButton = document.createElement("button");
        cancelButton.className = getButtonCssClassName("outline-black");
        cancelButton.innerHTML = "Cancel";

        if (showButton) {
            footer.insertBefore(cancelButton, doneButton);
        } else {
            footer.appendChild(cancelButton);
        }

        footer.className += " cancel";

        const cancelHandler = () => eventHandler(onCancel, "onCancel");
        cancelButton.addEventListener("click", cancelHandler);
    }

    document.body.appendChild(dialog);

    dialogPolyfill.registerDialog(dialog);

    if (open) {
        dialog.showModal();
    }

    return dialog;
}

export const ModalAlert = {
    alert(title: string, body: string | Array<string>, options: any = {}) {
        const props = {
            title: title,
            body: body,
            icon: ModalIcon.info,
            buttonText: "OK",
            ...options
        };

        return ShowModal(props);
    },

    success(title: string, body: string | Array<string>, options: any = {}) {
        const props = {
            icon: ModalIcon.tick,
            ...options
        };

        return this.alert(title, body, props);
    },

    loading(body: string) {
        return ShowModal({
            body: body,
            showButton: false,
            appendSpinner: true,
            showClose: false
        });
    },

    error(body: string | Array<string>, title = "Error", options: any = {}) {
        options["icon"] = ModalIcon.error;
        options["showClose"] = false;
        return this.alert(title, body, options);
    },

    permissionsError(onClose: any, message: string | Array<string> = Messages.noAccess) {
        ModalAlert.error(message, Messages.noAccessTitle, { onClose: onClose });
    },

    closeAll() {
        const dialogs = document.getElementsByClassName("ui-modal-dialog");
        for (let i = 0; i < dialogs.length; i++) {
            dialogs[i].remove();
        }
    },

    close(dialog: HTMLDialogElement) {
        if (_.isNil(dialog)) {
            return;
        }

        try {
            dialog.close();
            // document.body.removeChild(dialog);
            dialog.remove();
        } catch (error) {
            console.error("Error removing dialog from DOM");
        }
    }
}

export default { ShowModal, ModalAlert, ModalIcon };
